import root from 'window-or-global';
import { generateRoutePath } from 'epic-common/src/route';
import moment from 'moment';

import { GAME_TITLE } from '../constants/GameTitle';
import { LOGOS } from '../constants/logos';

export function getAssetPath(asset) {
    return `${root.__webpack_asset_path || ''}${asset}`;
}

export function trackProgress() {
    if (typeof root.com.epicgames !== 'undefined' && typeof root.com.epicgames.web !== 'undefined' && !!root.com.epicgames.web.track) {
        root.com.epicgames.web.track.loadPixel();
    }
}

export function loadScript(url) {
    if (typeof document === 'undefined') {
        console.log('FortniteUtils loadScript skipped, document not available');
        return;
    }
    const scriptTag = document.createElement('script');
    scriptTag.setAttribute('type', 'text/javascript');
    scriptTag.setAttribute('src', url);
    document.head.appendChild(scriptTag);
}

export function canPlayVideo() {
    if (typeof document === 'undefined') {
        console.log('FortniteUtils canPlayVideo: false, document not available');
        return false;
    }
    let canPlay;
    const video = document.createElement('video');
    if (video.canPlayType && video.canPlayType('video/webm') !== '') {
        canPlay = 'webm';
    } else if (video.canPlayType && video.canPlayType('video/mp4') !== '') {
        canPlay = 'mp4';
    } else {
        canPlay = 'none';
    }
    return canPlay;
}

export function getMetaTags(localizationStore) {
    const locale = localizationStore.get('locale');
    const messages = localizationStore.get('messageProperties');
    // Social metadata
    const socialMeta = {
        siteName: messages.get('epic.enterprise.name'),
        description: messages.get('epic.enterprise.common.description'),
        twitterDesc: messages.get('epic.enterprise.common.description'),
        locale: locale ? locale.replace('-', '_') : locale
    };

    let baseUrl;
    if (typeof document === 'undefined') {
        baseUrl = root.location.origin;
    } else {
        baseUrl = document.getElementsByTagName('html')[0].dataset
            ? document.getElementsByTagName('html')[0].dataset.baseurl
            : document.getElementsByTagName('html')[0].getAttribute('data-baseurl');
    }

    //if ends with /enterprise add a slash
    if (!baseUrl.match(/.*\/$/)) {
        baseUrl += '/';
    }

    const ogUrl = root.location.href;

    const tags = [
        //Facebook
        {property: 'og:url', content: ogUrl},
        {property: 'og:title', content: socialMeta.siteName},
        {property: 'og:description', content: socialMeta.description},
        {property: 'og:site_name', content: socialMeta.siteName},
        {property: 'og:locale', content: socialMeta.locale},
        {property: 'og:image', content: ''},

        //Twitter
        {name: 'twitter:card', content: 'summary'},
        {name: 'twitter:title', content: socialMeta.siteName},
        {name: 'twitter:description', content: socialMeta.twitterDesc},
        {name: 'twitter:image', content: ''}
    ];
    return tags;
}

export function getDateParams(input) {
    const date = new Date(input);
    const time = date.getTime();
    const year = date.getFullYear();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const month = date.getMonth();
    const enMonth = months[month + 1];
    const day = date.getDate() < 9 ? `0${date.getDate()}` : date.getDate();
    const hour = date.getHours();
    const min = date.getMinutes();
    const sec = date.getSeconds();


    return { date, time, year, month, enMonth, day, hour, min, sec };
}

export function parseHTML(string) {
    const html = document.createElement('div');
    html.innerHTML = string;
    return html;
}

/**
 * If the delta for the x or y coordinates of the mouse have changed from mouse down to mouse up
 *  it is dragging motion and not intended to be a click.
 * @param  {Object} e Event object
 * @return {Boolean}   true/false
 */
export function draggingMotion(e, mouseDown) {
    const allowed = 40;
    const x = e.clientX;
    const y = e.clientY;

    if (mouseDown) {
        if (Math.abs(x - mouseDown.x) > allowed) {
            return true;
        }

        if (Math.abs(y - mouseDown.y) > allowed) {
            return true;
        }
    }

    return false;
}

export function getSurvivorNavLink(isLoggedIn, commander) {
    if (isLoggedIn && commander) {
        return generateRoutePath('/search-for-survivors/rewards', true);
    }
    return generateRoutePath('/search-for-survivors', true);
}

/**
 * Fisher-Yates shuffle function.  This shuffle is done in-place and is destructive
 * @param array
 */
export function shuffle(array) {
    let unShuffled = array.length;
    let element = null;
    let currentElement;

    // While there remain elements to shuffle…
    while (unShuffled) {
        // Pick a remaining element…
        currentElement = Math.floor(Math.random() * unShuffled--);

        // And swap it with the current element.
        element = array[unShuffled];
        array[unShuffled] = array[currentElement];
        array[currentElement] = element;
    }

    return array;
}

/**
 * Get locale from path
 * @param location
 */
export function getCurrentLocale(location) {
    if (location.pathname.length && location.pathname.split('/').length >= 3) {
        return location.pathname.split('/')[2];
    }
    return '';
}

/**
 * Put account info on member
 * @param members
 * @param accounts
 */
export function mapAccountInfoToMember(members, accounts, entitlementGroup) {
    members.forEach(member => {
        const account = accounts.find(acc => {
            return acc.id === member.epicAccountId;
        }) || {};
        member.displayName = account.displayName;
        member.name = account.name;
        member.lastName = account.lastName;
        member.email = account.email;
        const entitledMembers = entitlementGroup.members || [];
        member.isSubscriber = !!entitledMembers.find(m => m.accountId === member.epicAccountId);
    });
    return members;
}

export function isExpired(endDate) {
    return (new Date(endDate)).getTime() < (new Date()).getTime();
}

export function getError(resp) {
    if (resp.error) {
        return resp.error.data || resp.error.response.data || {};
    }
    return {};
}

export function getDuration(m, format = 'hours') {
    const _duration = moment.duration(moment.utc(new Date()) - moment.utc(m));
    return _duration[format]();
}

export function getCookie(name) {
    const cookie = {};
    document.cookie.split(';').forEach((el) => {
        const [k, v] = el.split('=');
        cookie[k.trim()] = v;
    });
    return cookie[name];
}

export function getURLParam(param) {
    return root.URLSearchParams && new root.URLSearchParams(root.location.search).get(param);
}

export function generateURLParam(param, value, pathName) {
    const currentURL = root.location.href;
    if (root.URL) {
        const _url = new root.URL(currentURL);
        if (getURLParam(param)) {
            _url.searchParams.set(param, value);
        } else {
            _url.searchParams.append(param, value);
        }
        if (pathName) {
            return _url.toString().replace(_url.pathname, pathName);
        }
        return _url.toString();
    }
    return '';
}

const IMAGE_MAP = {
    [GAME_TITLE.FORTNITE]: LOGOS.FN,
    [GAME_TITLE.ROCKET_LEAGUE]: LOGOS.RL,
    [GAME_TITLE.SIDE_SWIPE]: LOGOS.SS,
    [GAME_TITLE.FALL_GUYS]: LOGOS.FG
};

const GAME_TITLE_MAP = {
    [GAME_TITLE.FORTNITE]: 'Fortnite',
    [GAME_TITLE.ROCKET_LEAGUE]: 'Rocket League',
    [GAME_TITLE.SIDE_SWIPE]: 'Rocket League Sideswipe',
    [GAME_TITLE.FALL_GUYS]: 'Fall Guys'
};

export const getGameImage = (gameId) => IMAGE_MAP[gameId];

export const getGameTitle = (gameId) => GAME_TITLE_MAP[gameId];

export const getGameLogoClass = (gameId) => {
    switch (gameId) {
        case GAME_TITLE.ROCKET_LEAGUE:
            return 'prize__game--rl';
        case GAME_TITLE.SIDE_SWIPE:
            return 'prize__game--ss';
        case GAME_TITLE.FALL_GUYS:
            return 'prize__game--fg';
        default:
            return 'prize__game--fn';
    }
};

export const GAME_GATED_USER_AGE = {
    [GAME_TITLE.FORTNITE]: 13,
    [GAME_TITLE.FALL_GUYS]: 13,
    [GAME_TITLE.ROCKET_LEAGUE]: 13,
    [GAME_TITLE.SIDE_SWIPE]: 15
};

const getAgeDiff = (dateOfBirth, year) => (moment().diff(dateOfBirth, 'years') < year);
export const getUserIsCabinedMode = (accountInfo) => accountInfo.cabinedMode;
export const getUserIsGated = (accountInfo, gameId) => {
    const gateUserAge = GAME_GATED_USER_AGE[gameId] || 13;
    return accountInfo.dateOfBirth ? getAgeDiff(accountInfo.dateOfBirth, gateUserAge) : false;
};
export const getUserIsMinor = (accountInfo) => {
    return accountInfo.dateOfBirth ? getAgeDiff(accountInfo.dateOfBirth, 18) : false;
};

const getIsPrizeGated = (gameId, elapsedYears) => (elapsedYears < GAME_GATED_USER_AGE[gameId]);
export const processUserAge = (prizes, dateOfBirth) => {
    if (!prizes.length) {
        return [];
    }
    if (dateOfBirth) {
        const elapsedYears = moment().diff(dateOfBirth, 'years');
        return prizes.map((prize) => {
            prize.isPrizeGated = getIsPrizeGated(prize.gameId, elapsedYears);
            return prize;
        });
    }
    return prizes;
};
